"use client";

import { useMutation as useDeleteHiddenBrandUserFeedback } from "@faire/web-api/api/v2/search/feedback/hide-brand/brandToken/delete-hooks";
import { trackProductTileHideBrandUndo } from "@faire/web-api/events/productTile/actionUnknown/hideBrandUndo";
import { trackProductTileHideBrandClick } from "@faire/web-api/events/productTile/click/hideBrand";
import { trackProductTileUserFeedbackClick } from "@faire/web-api/events/productTile/click/userFeedback";
import { trackProductTileHideBrandSubmit } from "@faire/web-api/events/productTile/submit/hideBrand";
import { Row } from "@faire/web/slate/Layout";
import React, { useContext } from "react";

import { SearchUserDrivenFeedbackTooltipClientWrapper } from "@faire/retailer/components/UserFeedback/SearchUserDrivenFeedbackTooltipClientWrapper";
import { UserDrivenFeedbackButton } from "@faire/retailer/components/UserFeedback/UserDrivenFeedbackButton";
import { UserDrivenFeedbackProductTileUndoOverlay } from "@faire/retailer/components/UserFeedback/UserDrivenFeedbackProductTileUndoOverlay";
import { useUserFeedbackContextStore } from "@faire/retailer/components/UserFeedback/UserFeedbackProductTileContext";
import { ProductPositioningContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/productTileContext";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useSettingEnableUserFeedback } from "@faire/retailer/settings/useSettingEnableUserFeedback";

interface IProps {
  brandToken?: string;
  index?: number;
  tileToken?: string;
  showTooltip?: boolean;
  requestId?: string;
  impressionId?: string;
}

export const MaybeComponentWithUserDrivenFeedbackButton: React.FC<
  React.PropsWithChildren<IProps>
> = ({
  children,
  brandToken,
  index,
  showTooltip,
  requestId,
  tileToken,
  impressionId,
}) => {
  const isUserLoggedIn = useIsLoggedInRetailer();
  const settingValue = useSettingEnableUserFeedback();
  const { hiddenBrandTokens, addHiddenBrand, removeHiddenBrand } =
    useUserFeedbackContextStore();

  const { mutate: undoHiddenBrand } = useDeleteHiddenBrandUserFeedback({
    onSuccess: () => {
      removeHiddenBrand(brandToken ?? "");
      trackProductTileHideBrandUndo(
        requestId ?? "",
        brandToken ?? "",
        tileToken ?? "",
        String(index ?? -1),
        impressionId ?? ""
      );
    },
  });

  const onButtonClick = () =>
    trackProductTileUserFeedbackClick(
      requestId ?? "",
      brandToken ?? "",
      tileToken ?? "",
      String(index ?? -1),
      impressionId ?? ""
    );

  const onHideBrandClicked = () =>
    trackProductTileHideBrandClick(
      requestId ?? "",
      brandToken ?? "",
      tileToken ?? "",
      String(index ?? -1),
      impressionId ?? ""
    );

  const onHideBrandSuccess = (reason?: string) => {
    trackProductTileHideBrandSubmit(
      requestId ?? "",
      brandToken ?? "",
      tileToken ?? "",
      String(index ?? -1),
      reason ?? "",
      impressionId ?? ""
    );
    addHiddenBrand(brandToken ?? "");
  };

  const buttonComponent =
    index !== undefined && showTooltip ? (
      <SearchUserDrivenFeedbackTooltipClientWrapper index={index}>
        <UserDrivenFeedbackButton
          brandToken={brandToken}
          onButtonClicked={onButtonClick}
          onHideBrandClicked={onHideBrandClicked}
          onHideBrandSuccess={onHideBrandSuccess}
        />
      </SearchUserDrivenFeedbackTooltipClientWrapper>
    ) : (
      <UserDrivenFeedbackButton
        onButtonClicked={onButtonClick}
        onHideBrandClicked={onHideBrandClicked}
        brandToken={brandToken}
        onHideBrandSuccess={onHideBrandSuccess}
      />
    );

  if (settingValue && isUserLoggedIn) {
    return (
      <>
        {hiddenBrandTokens.includes(brandToken ?? "") ? (
          <UserDrivenFeedbackProductTileUndoOverlay
            onUndoClick={() => undoHiddenBrand([brandToken ?? ""])}
          />
        ) : null}
        <Row justify="space-between">
          {children}
          {buttonComponent}
        </Row>
      </>
    );
  }
  return <>{children}</>;
};
/**
 * Since the ProductPositioningContext is not provided in all cases, we will conditionally render this component in places where the context is present.
 * Otherwise we render the component above.
 */
export const MaybeComponentWithUserDrivenFeedbackButtonWithIndexConsumer: React.FC<
  React.PropsWithChildren<Omit<IProps, "index">>
> = (props) => {
  const positionContext = useContext(ProductPositioningContext);
  return (
    <MaybeComponentWithUserDrivenFeedbackButton
      {...props}
      index={positionContext.index}
    />
  );
};
